// REACT_APP_API_URL is baked in at build time. The fallback used to be a
// long-dead Replit sandbox URL, so a production build made without that
// variable set silently pointed the whole app at a host that no longer exists —
// every request failing with a network error rather than anything diagnosable.
// Falling back to a same-origin /api keeps a misconfigured build talking to
// something plausible, and the warning below makes the misconfiguration loud.
const DEFAULT_API_URL =
  typeof window !== 'undefined' ? `${window.location.origin}/api` : 'http://localhost:8000/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || DEFAULT_API_URL;

if (!process.env.REACT_APP_API_URL && process.env.NODE_ENV === 'production') {
  console.warn(
    `REACT_APP_API_URL was not set at build time. Falling back to ${API_BASE_URL}. ` +
    'Set it in the build environment if the API is on a different host.'
  );
}

class APIService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Helper function to check if token is valid (not expired)
  isTokenValid(token) {
    if (!token) return false;
    
    try {
      // Decode the JWT token to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch (error) {
      console.error('Error parsing token:', error);
      return false;
    }
  }

  // Get auth headers with token.
  // For multipart uploads pass isFormData=true so we DON'T set Content-Type —
  // the browser must set it to multipart/form-data with the correct boundary.
  getAuthHeaders(isFormData = false) {
    const token = localStorage.getItem('token');

    const headers = isFormData ? {} : { 'Content-Type': 'application/json' };

    if (token && this.isTokenValid(token)) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  // Build a FormData body from a plain object. File/Blob values are appended as
  // files; everything else is stringified. Arrays append repeated keys.
  static toFormData(obj) {
    const fd = new FormData();
    Object.entries(obj || {}).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      if (Array.isArray(value)) {
        value.forEach((v) => fd.append(key, v));
      } else {
        fd.append(key, value);
      }
    });
    return fd;
  }

  // Exchange the refresh token for a fresh access token. Single-flight: many
  // requests firing while the access token is expired share one refresh call.
  // Returns true if a new access token was stored.
  async performTokenRefresh() {
    const refresh = localStorage.getItem('refreshToken');
    if (!refresh || !this.isTokenValid(refresh)) {
      return false;
    }

    if (!this._refreshPromise) {
      this._refreshPromise = (async () => {
        try {
          const res = await fetch(`${this.baseURL}/users/token/refresh/`, {
            method: 'POST',
            credentials: 'omit',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh }),
          });
          if (!res.ok) return false;
          const data = await res.json();
          if (data.access) {
            localStorage.setItem('token', data.access);
            // Refresh tokens rotate (BLACKLIST_AFTER_ROTATION), so store the new one.
            if (data.refresh) localStorage.setItem('refreshToken', data.refresh);
            return true;
          }
          return false;
        } catch (e) {
          return false;
        } finally {
          this._refreshPromise = null;
        }
      })();
    }
    return this._refreshPromise;
  }

  // Tokens are unusable — clear them and let the app react (AuthContext
  // listens for this and logs the user out).
  forceLogout() {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
  }

  // Generic API request method
  async request(endpoint, options = {}, _isRetry = false) {
    const url = `${this.baseURL}${endpoint}`;
    let token = localStorage.getItem('token');

    // Public (pre-auth) endpoints: these are how a user OBTAINS a session, so
    // they must never be gated behind "must already be logged in" — otherwise
    // the client short-circuits with "Authentication required" before the
    // request is even sent (e.g. social sign-in exchanges a Google token for
    // our JWT while the user is, by definition, not yet authenticated).
    const PUBLIC_ENDPOINTS = [
      '/login/',
      '/register/',
      '/token/refresh/',
      '/social-auth/',
      '/password-reset/',
      '/password-reset-confirm/',
    ];
    const isProtectedEndpoint = !PUBLIC_ENDPOINTS.some((p) => endpoint.includes(p));

    // If the access token is expired on a protected call, try to refresh it
    // once before giving up — this keeps a session alive past the 60-min
    // access-token lifetime instead of hard-failing every call.
    if (isProtectedEndpoint && (!token || !this.isTokenValid(token))) {
      const refreshed = await this.performTokenRefresh();
      if (!refreshed) {
        this.forceLogout();
        throw new Error('Authentication required');
      }
      token = localStorage.getItem('token');
    }

    const isFormData = options.body instanceof FormData;
    const config = {
      // Auth is pure JWT via the Authorization header — the API never needs
      // cookies. Omitting them stops a same-origin Django admin `sessionid`
      // cookie from riding along and tripping DRF's CSRF check on login.
      credentials: 'omit',
      // For a FormData body, let the browser set the multipart Content-Type
      // (with boundary) — forcing application/json would corrupt file uploads.
      headers: this.getAuthHeaders(isFormData),
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // The server rejected the token (e.g. rotated/blacklisted). Refresh once
      // and retry the original request before surfacing an auth error.
      if (response.status === 401 && isProtectedEndpoint && !_isRetry) {
        const refreshed = await this.performTokenRefresh();
        if (refreshed) {
          return this.request(endpoint, options, true);
        }
        this.forceLogout();
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Build the message, then attach the status and raw body so callers
        // can branch on them (e.g. 402 = not enough credits).
        let message;

        // Handle validation errors
        if (response.status === 400 && errorData) {
          // If there are field-specific errors, format them nicely
          const errors = [];
          for (const [field, messages] of Object.entries(errorData)) {
            if (Array.isArray(messages)) {
              errors.push(`${field}: ${messages.join(', ')}`);
            } else if (typeof messages === 'string') {
              errors.push(`${field}: ${messages}`);
            }
          }
          if (errors.length > 0) {
            message = errors.join('; ');
          }
        }

        // The backend returns errors under `error` far more often than
        // `detail`/`message`, so check it too before falling back.
        const apiError = typeof errorData.error === 'string' ? errorData.error : null;
        const error = new Error(
          message ||
          errorData.detail ||
          errorData.message ||
          apiError ||
          `HTTP error! status: ${response.status}`
        );
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      // Handle 204 No Content responses
      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      // Only log network errors, not user-initiated cancellations
      if (error.name !== 'AbortError') {
        console.error('API request failed:', error);
      }
      
      // Enhance error messages for common network issues
      if (error.message === 'Failed to fetch') {
        throw new Error(
          'Unable to connect to the server. This could be due to:\n' +
          '• Network connection issues\n' +
          '• Server is not responding\n' +
          '• CORS configuration issue on the backend\n\n' +
          'Please check your internet connection and ensure the backend server is running.'
        );
      }
      
      if (error.name === 'TypeError' && error.message.includes('NetworkError')) {
        throw new Error(
          'Network error occurred. Please check your internet connection and try again.'
        );
      }
      
      throw error;
    }
  }

  // Authentication endpoints
  async login(email, password) {
    return this.request('/users/login/', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  // Social sign-in: exchange a provider token for our JWT.
  async socialAuth(provider, accessToken) {
    return this.request('/users/social-auth/', {
      method: 'POST',
      body: JSON.stringify({ provider, access_token: accessToken }),
    });
  }

  async signup(email, password, firstName, lastName, userType, passwordConfirm) {
    return this.request('/users/register/', {
      method: 'POST',
      body: JSON.stringify({
        email,
        username: email, // Use email as username
        password,
        password_confirm: passwordConfirm,
        first_name: firstName,
        last_name: lastName,
        is_handler: userType === 'handler',
        is_owner: userType === 'owner',
      }),
    });
  }

  async logout() {
    // The backend blacklists the refresh token, which is what actually ends
    // the session — an access token alone stays valid until it expires.
    return this.request('/users/logout/', {
      method: 'POST',
      body: JSON.stringify({ refresh: localStorage.getItem('refreshToken') }),
    });
  }

  // Password reset. The backend has always exposed these two endpoints and
  // emailed a link to {FRONTEND_URL}/reset-password, but the client had no
  // methods for them and the app had no such route — the flow dead-ended.
  async requestPasswordReset(email) {
    return this.request('/users/password-reset/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async confirmPasswordReset(uid, token, newPassword) {
    return this.request('/users/password-reset/confirm/', {
      method: 'POST',
      body: JSON.stringify({ uid, token, new_password: newPassword }),
    });
  }

  async getCurrentUser() {
    return this.request('/users/profile/');
  }

  async getCurrentUserProfile() {
    // Alias for getCurrentUser() - returns current user profile with address info
    return this.request('/users/profile/');
  }

  async refreshToken() {
    const refreshToken = localStorage.getItem('refreshToken');
    return this.request('/users/token/refresh/', {
      method: 'POST',
      body: JSON.stringify({ refresh: refreshToken }),
    });
  }

  // User endpoints
  async getUser(userId) {
    return this.request(`/users/${userId}/`);
  }

  async updateUser(userId, userData) {
    return this.request(`/users/${userId}/`, {
      method: 'PATCH',
      body: JSON.stringify(userData),
    });
  }

  // Handler profile endpoints
  async getHandlerProfiles() {
    return this.request('/users/handlers/');
  }

  async getHandlerProfile(handlerId) {
    return this.request(`/users/handlers/${handlerId}/`);
  }

  async createHandlerProfile(profileData) {
    return this.request('/users/profile/handler/', {
      method: 'POST',
      body: JSON.stringify(profileData),
    });
  }

  async updateHandlerProfile(userId, profileData) {
    return this.request(`/users/handlers/${userId}/`, {
      method: 'PATCH',
      body: JSON.stringify(profileData),
    });
  }

  async getCurrentUserHandlerProfile() {
    // Get current user's handler profile with all fields including verification status
    return this.request('/users/profile/handler/');
  }

  async updateCurrentUserHandlerProfile(profileData) {
    return this.request('/users/profile/handler/', {
      method: 'PATCH',
      body: JSON.stringify(profileData),
    });
  }

  async toggleHandlerAvailability(userId) {
    return this.request(`/users/handlers/${userId}/toggle_availability/`, {
      method: 'POST',
    });
  }

  // Service endpoints
  async getServices(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/services/?${queryString}` : '/services/';
    return this.request(endpoint);
  }

  async getServiceTypes(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/services/types/?${queryString}` : '/services/types/';
    return this.request(endpoint);
  }

  async getServiceCategories() {
    return this.request('/services/categories/');
  }

  async getService(serviceId) {
    return this.request(`/services/${serviceId}/`);
  }

  async createService(serviceData) {
    return this.request('/services/', {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
  }

  async updateService(serviceId, serviceData) {
    return this.request(`/services/${serviceId}/`, {
      method: 'PATCH',
      body: JSON.stringify(serviceData),
    });
  }

  // Service booking endpoints
  async getServiceBookings(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/services/bookings/?${queryString}` : '/services/bookings/';
    return this.request(endpoint);
  }

  async getServiceBooking(bookingId) {
    return this.request(`/services/bookings/${bookingId}/`);
  }

  async createServiceBooking(bookingData) {
    return this.request('/services/bookings/', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }

  async updateServiceBooking(bookingId, bookingData) {
    return this.request(`/services/bookings/${bookingId}/`, {
      method: 'PATCH',
      body: JSON.stringify(bookingData),
    });
  }

  // Service management actions
  async cancelServiceBooking(bookingId) {
    return this.request(`/services/bookings/${bookingId}/`, {
      method: 'DELETE',
    });
  }

  async cancelServiceBookingWithFee(bookingId) {
    return this.request(`/services/bookings/${bookingId}/cancel/`, {
      method: 'POST',
    });
  }

  async deleteServiceBooking(bookingId) {
    return this.request(`/services/bookings/${bookingId}/`, {
      method: 'DELETE',
    });
  }

  // Handler-specific endpoints
  async getHandlerRequests() {
    return this.request('/services/handler-requests/');
  }

  async getNearbyRequests(maxDistance = null) {
    const params = maxDistance ? { max_distance: maxDistance } : {};
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/services/nearby-requests/?${queryString}` : '/services/nearby-requests/';
    return this.request(endpoint);
  }

  // Service request endpoints
  async getServiceRequests() {
    return this.request('/services/requests/');
  }

  async createServiceRequest(requestData) {
    return this.request('/services/requests/', {
      method: 'POST',
      body: JSON.stringify(requestData),
    });
  }

  async getServiceRequest(requestId) {
    return this.request(`/services/requests/${requestId}/`);
  }

  async updateServiceRequest(requestId, requestData) {
    return this.request(`/services/requests/${requestId}/`, {
      method: 'PATCH',
      body: JSON.stringify(requestData),
    });
  }

  async acceptServiceRequest(requestId) {
    return this.request(`/services/requests/${requestId}/respond/`, {
      method: 'POST',
      body: JSON.stringify({ action: 'accept' }),
    });
  }

  async rejectServiceRequest(requestId) {
    return this.request(`/services/requests/${requestId}/respond/`, {
      method: 'POST',
      body: JSON.stringify({ action: 'decline' }),
    });
  }

  // Service update endpoints
  async getServiceUpdates(bookingId) {
    return this.request(`/services/bookings/${bookingId}/updates/`);
  }

  async createServiceUpdate(bookingId, updateData) {
    // A chat photo is a File — send multipart so the bytes actually upload.
    const hasFile = updateData?.photo instanceof File;
    return this.request(`/services/bookings/${bookingId}/updates/`, {
      method: 'POST',
      body: hasFile
        ? APIService.toFormData(updateData)
        : JSON.stringify(updateData),
    });
  }

  // Dashboard endpoints
  async getOwnerDashboard() {
    return this.request('/services/dashboard/owner/');
  }

  async getHandlerDashboard() {
    return this.request('/services/dashboard/handler/');
  }

  // Pet endpoints
  async getPets() {
    return this.request('/pets/');
  }

  async getPet(petId) {
    return this.request(`/pets/${petId}/`);
  }

  async createPet(petData) {
    return this.request('/pets/', {
      method: 'POST',
      body: JSON.stringify(petData),
    });
  }

  async updatePet(petId, petData) {
    return this.request(`/pets/${petId}/`, {
      method: 'PATCH',
      body: JSON.stringify(petData),
    });
  }

  async deletePet(petId) {
    return this.request(`/pets/${petId}/`, {
      method: 'DELETE',
    });
  }

  async getPetOptions() {
    return this.request('/pets/options/');
  }

  // Service booking endpoints (walks)
  async getWalks(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/services/bookings/?${queryString}` : '/services/bookings/';
    return this.request(endpoint);
  }

  async getWalk(walkId) {
    return this.request(`/services/bookings/${walkId}/`);
  }

  async createWalk(walkData) {
    return this.request('/services/bookings/', {
      method: 'POST',
      body: JSON.stringify(walkData),
    });
  }

  async updateWalk(walkId, walkData) {
    return this.request(`/services/bookings/${walkId}/`, {
      method: 'PATCH',
      body: JSON.stringify(walkData),
    });
  }

  async deleteWalk(walkId) {
    return this.request(`/services/bookings/${walkId}/`, {
      method: 'DELETE',
    });
  }

  async cancelWalkWithFee(walkId) {
    return this.request(`/services/bookings/${walkId}/cancel/`, {
      method: 'POST',
    });
  }

  async getWalkUpdates(walkId) {
    return this.request(`/services/bookings/${walkId}/updates/`);
  }

  async getHandlerHistory(limit = null) {
    const params = limit ? `?limit=${limit}` : '';
    return this.request(`/services/handler/history${params}`);
  }

  // Review endpoints
  async getReviews() {
    return this.request('/reviews/handler/');
  }

  async createReview(reviewData) {
    return this.request('/reviews/handler/', {
      method: 'POST',
      body: JSON.stringify(reviewData),
    });
  }



  async createHandlerReview(reviewData) {
    return this.request('/reviews/handler/', {
      method: 'POST',
      body: JSON.stringify(reviewData),
    });
  }

  async createPetReview(reviewData) {
    return this.request('/pets/pet-reviews/', {
      method: 'POST',
      body: JSON.stringify(reviewData),
    });
  }

  async getHandlerReviews(handlerId) {
    return this.request(`/reviews/handler/?handler_id=${handlerId}`);
  }

  async getOwnerReviews() {
    return this.request('/reviews/owner/');
  }

  async createOwnerReview(reviewData) {
    return this.request('/reviews/owner/', {
      method: 'POST',
      body: JSON.stringify(reviewData),
    });
  }

  // Search and filter endpoints
  async searchHandlers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/users/handlers/?${queryString}`);
  }

  async searchPets(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/pets/?${queryString}`);
  }

  // Location/geocoding endpoints
  async geocodeAddress(address) {
    return this.request('/users/geocode/', {
      method: 'POST',
      body: JSON.stringify({ address }),
    });
  }

  async reverseGeocode(lat, lng) {
    return this.request('/users/reverse-geocode/', {
      method: 'POST',
      body: JSON.stringify({ lat, lng }),
    });
  }

  // Address book endpoints
  async getAddressBook() {
    return this.request('/users/addresses/');
  }

  async createAddress(addressData) {
    return this.request('/users/addresses/', {
      method: 'POST',
      body: JSON.stringify(addressData),
    });
  }

  async updateAddress(addressId, addressData) {
    return this.request(`/users/addresses/${addressId}/`, {
      method: 'PATCH',
      body: JSON.stringify(addressData),
    });
  }

  async deleteAddress(addressId) {
    return this.request(`/users/addresses/${addressId}/`, {
      method: 'DELETE',
    });
  }

  async setDefaultAddress(addressId) {
    return this.request(`/users/addresses/${addressId}/set_default/`, {
      method: 'POST',
    });
  }

  // Generic HTTP methods for flexibility
  async get(endpoint) {
    return this.request(endpoint);
  }

  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }

  // Profile update methods
  async updateProfile(profileData) {
    // If a profile picture File is included, send multipart; else JSON.
    const hasFile = profileData?.profile_picture instanceof File;
    return this.request('/users/profile/', {
      method: 'PATCH',
      body: hasFile
        ? APIService.toFormData(profileData)
        : JSON.stringify(profileData),
    });
  }



  // Search handler services (with service type filtering)
  async searchHandlerServices(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/services/?${queryString}`);
  }

  // Handler application endpoints.
  //
  // These previously pointed at /users/handlers/apply/ and
  // /users/handlers/application-status/ — routes that have never existed on
  // the backend (they resolved into the handlers viewset's detail route and
  // answered 405/404). Submissions could not succeed at all, and the status
  // poll's 404 was swallowed as "no application yet". The real routes are the
  // handler-applications viewset and the status endpoint below.
  async submitHandlerApplication(applicationData) {
    // The application carries file uploads (certified ID copy, proof of
    // address, bank letter…), so send multipart when any File is present —
    // JSON would drop the files.
    const hasFile = Object.values(applicationData || {}).some(
      (value) => value instanceof File
    );
    return this.request('/users/handler-applications/', {
      method: 'POST',
      body: hasFile
        ? APIService.toFormData(applicationData)
        : JSON.stringify(applicationData),
    });
  }

  async getHandlerApplicationStatus() {
    return this.request('/users/handler-application-status/');
  }

  // Payment method endpoints
  async getPaymentMethods() {
    return this.request('/billing/payment-methods/');
  }

  async createPaymentMethod(paymentMethodData) {
    return this.request('/billing/payment-methods/', {
      method: 'POST',
      body: JSON.stringify(paymentMethodData),
    });
  }

  async updatePaymentMethod(paymentMethodId, paymentMethodData) {
    return this.request(`/billing/payment-methods/${paymentMethodId}/`, {
      method: 'PATCH',
      body: JSON.stringify(paymentMethodData),
    });
  }

  async deletePaymentMethod(paymentMethodId) {
    return this.request(`/billing/payment-methods/${paymentMethodId}/`, {
      method: 'DELETE',
    });
  }

  async setDefaultPaymentMethod(paymentMethodId) {
    return this.request(`/billing/payment-methods/${paymentMethodId}/set_default/`, {
      method: 'POST',
    });
  }

  // Payment processing endpoints
  async processPayment(paymentData) {
    return this.request('/billing/payments/process/', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  }

  async createPaymentIntent(intentData) {
    return this.request('/billing/payment-intents/', {
      method: 'POST',
      body: JSON.stringify(intentData),
    });
  }

  async confirmPaymentIntent(intentId, confirmData) {
    return this.request(`/billing/payment-intents/${intentId}/confirm/`, {
      method: 'POST',
      body: JSON.stringify(confirmData),
    });
  }

  async verifyPaymentMethod(paymentMethodId) {
    return this.request(`/billing/payment-methods/${paymentMethodId}/verify/`, {
      method: 'POST',
    });
  }

  // Transaction endpoints
  async getTransactions(filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    const endpoint = queryString ? `/billing/transactions/?${queryString}` : '/billing/transactions/';
    return this.request(endpoint);
  }

  async getTransaction(transactionId) {
    return this.request(`/billing/transactions/${transactionId}/`);
  }

  async requestRefund(transactionId, refundData) {
    return this.request(`/billing/transactions/${transactionId}/refund/`, {
      method: 'POST',
      body: JSON.stringify(refundData),
    });
  }

  // Payment receipt endpoints
  async getTransactionReceipt(transactionId) {
    return this.request(`/billing/transactions/${transactionId}/receipt/`);
  }

  async downloadTransactionReceipt(transactionId) {
    return this.request(`/billing/transactions/${transactionId}/receipt/download/`);
  }

  // ---- Admin portal (staff only; backend enforces IsAdminUser) ----
  async getAdminStats() {
    return this.request('/admin/stats/');
  }

  async getAdminUsers({ search = '', role = '' } = {}) {
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    if (role) qs.set('role', role);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.request(`/admin/users/${suffix}`);
  }

  async updateAdminUser(userId, data) {
    return this.request(`/admin/users/${userId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getAdminServiceTypes() {
    return this.request('/admin/service-types/');
  }

  // Field edits go as JSON; image uploads (icon_image / background_image File)
  // switch to multipart so the browser sets the multipart boundary.
  async updateAdminServiceType(serviceTypeId, data) {
    const hasFile =
      data?.icon_image instanceof File || data?.background_image instanceof File;
    return this.request(`/admin/service-types/${serviceTypeId}/`, {
      method: 'PATCH',
      body: hasFile ? APIService.toFormData(data) : JSON.stringify(data),
    });
  }

  async getAdminCategories() {
    return this.request('/admin/categories/');
  }

  // Handler applications (staff see all via the viewset's get_queryset).
  async getHandlerApplications() {
    return this.request('/users/handler-applications/');
  }

  async approveHandlerApplication(applicationId) {
    return this.request(`/users/handler-applications/${applicationId}/approve/`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async rejectHandlerApplication(applicationId, reason = '') {
    return this.request(`/users/handler-applications/${applicationId}/reject/`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }
}

// Create and export a singleton instance
const apiService = new APIService();
export default apiService; 
